/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import Link from 'next/link';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { NodeData, BLOCKS } from '@contentful/rich-text-types';
import { PageCTA } from './PageCTA';
import styles from './Article.module.scss';
import { ResponsiveImageBlock } from './ResponsiveImageBlock';
import { Title } from '../Title';
import { RichTextBlockContent } from '../../types/schema';

type ArticleContentType = {
  disclaimer?: string;
  showCta: boolean;
  title: string;
  content: RichTextBlockContent;
};

export const Article = ({ content, disclaimer, showCta, title }: ArticleContentType) => {
  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node: NodeData) => {
        const entry: any | undefined | null = content.links.assets.block.find(
          (element: any) => node?.data.target.sys.id === element.sys.id,
        );
        return entry ? (
          <ResponsiveImageBlock
            imageAlt={entry?.title}
            fullWidth={entry?.fullWidth}
            imageDesktopURL={entry?.url}
            desktopWidth={entry?.width}
            desktopHeight={entry?.height}
            desktopMaxWidth={672}
            imageMobileURL={entry?.url}
            mobileWidth={entry?.width}
            mobileHeight={entry?.height}
            mobileMaxWidth={460}
          />
        ) : null;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: NodeData) => {
        const entry: any | undefined | null = content.links.entries.block.find(
          (element: any) => node?.data.target.sys.id === element.sys.id,
        );
        return entry ? (
          <ResponsiveImageBlock
            imageAlt={entry?.imageAlt}
            fullWidth={entry?.fullWidth}
            imageDesktopURL={entry?.imageDesktop.url}
            desktopWidth={JSON.stringify(entry?.imageDesktop.width)}
            desktopHeight={JSON.stringify(entry?.imageDesktop.height)}
            desktopMaxWidth={672}
            imageMobileURL={entry?.imageMobile.url}
            mobileWidth={JSON.stringify(entry?.imageMobile.width)}
            mobileHeight={JSON.stringify(entry?.imageMobile.height)}
            mobileMaxWidth={460}
          />
        ) : null;
      },
      [BLOCKS.PARAGRAPH]: (_node: NodeData, children: React.ReactNode) => (
        <p style={{ marginBottom: '24px' }}>{children}</p>
      ),
      [BLOCKS.HEADING_1]: (_node: NodeData, children: React.ReactNode) => (
        <Title size="xl">
          <h1>{children}</h1>
        </Title>
      ),
      [BLOCKS.HEADING_2]: (_node: NodeData, children: React.ReactNode) => (
        <Title size="l">
          <h2>{children}</h2>
        </Title>
      ),
      [BLOCKS.HEADING_3]: (_node: NodeData, children: React.ReactNode) => (
        <Title size="m">
          <h3>{children}</h3>
        </Title>
      ),
      [BLOCKS.HEADING_4]: (_node: NodeData, children: React.ReactNode) => (
        <Title size="sm">
          <h4>{children}</h4>
        </Title>
      ),
      [BLOCKS.HEADING_5]: (_node: NodeData, children: React.ReactNode) => (
        <Title size="sm">
          <h5>{children}</h5>
        </Title>
      ),
      [BLOCKS.HEADING_6]: (_node: NodeData, children: React.ReactNode) => (
        <Title size="s">
          <h6>{children}</h6>
        </Title>
      ),
      [BLOCKS.TABLE]: (_node: NodeData, children: React.ReactNode) => (
        <div className="table">
          <table>
            <tbody>{children}</tbody>
          </table>
        </div>
      ),
    },
  };

  return (
    <>
      <div className={`${styles.root} ${styles.container}`} style={{ padding: '2rem 1rem', position: 'relative' }}>
        <Link href="/learn" className={styles.back}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <path
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 13L2 8l5-5M2 8h12"
            />
          </svg>{' '}
          Back
        </Link>
        <div className={styles['blog-column']}>
          <div className={styles.breadcrumb}>
            <Link href="/learn">Home Truths</Link> /<strong>{title}</strong>
          </div>
          {content && <div className={styles.content}>{documentToReactComponents(content.json, options)}</div>}
        </div>
      </div>

      {showCta && (
        <div style={{ position: 'relative' }}>
          <PageCTA />
        </div>
      )}

      {disclaimer && (
        <div style={{ padding: '16px' }}>
          <small>{disclaimer}</small>
        </div>
      )}
    </>
  );
};
